import * as React from 'react';
import isEqual from 'lodash/isEqual';
import { Filters, FilterGlobalState, FilterStateManager, FilterType, FilterChangeHandler } from '../types';

export const getFilterIDs = <T,>(filters: T) => Object.keys(filters || {}) as Array<keyof T>;

const makeEmptyState = <T extends FilterType>(filters: Filters<T>, newState: Partial<T> = {}) => {
  const entries = getFilterIDs(filters);
  const valuePairs = entries.map(id => [id, newState[id] || filters[id].initialValue]);

  return Object.fromEntries(valuePairs) as T;
};

const getFilterResetValue = <T extends FilterType>(
  filterId: keyof T,
  filters: Filters<T>,
  state: FilterGlobalState<T>,
) => {
  /* filter's initial value */
  const iv = filters[filterId].initialValue;
  /* value coming from onReset */
  const rv = filters[filterId].onReset?.(state.filterValues[filterId]);

  /* so here we can be sure we have a value  */
  return rv !== undefined ? rv : iv;
};

export const useInternalFilterState = <T extends FilterType>({
  filters,
  onChange,
  value,
  open,
}: {
  filters: Filters<T>;
  onChange?: FilterChangeHandler<T>;
  value?: Partial<T>;
  open?: boolean;
}) => {
  const [state, setState] = React.useState<FilterGlobalState<T>>({
    open: open || false,
    activeTab: getFilterIDs(filters)[0],
    filterValues: makeEmptyState(filters, value),
  });

  const actions: FilterStateManager<T> = {
    state,
    actions: {
      setState,
      hasActiveFilters: () =>
        !!Object.keys(state.filterValues).filter(k => !isEqual(state.filterValues[k], filters[k].initialValue)).length,
      getFilterValue: filterId => state.filterValues[filterId],
      makeSetFilterValue: filterId => value => {
        let computedValue = value;
        if (value instanceof Function) {
          computedValue = value(state.filterValues[filterId]);
        }

        if (computedValue === undefined) {
          return;
        }

        setState(s => ({ ...s, filterValues: { ...s.filterValues, [filterId]: computedValue } }));
        if (onChange) {
          onChange(
            { id: filterId, value: computedValue as T[keyof T] },
            { ...state.filterValues, [filterId]: computedValue },
          );
        }
      },
      setActiveFilter: filterId => {
        setState(s => ({ ...s, activeTab: filterId }));
      },
      resetFilter: filterId => {
        const value = getFilterResetValue(filterId, filters, state);
        setState(s => ({ ...s, filterValues: { ...s.filterValues, [filterId]: value } }));
        if (onChange) {
          onChange({ id: filterId, value }, { ...state.filterValues, [filterId]: value });
        }
      },
      showFilter: filterId =>
        setState(s => ({
          ...s,
          open: true,
          activeTab: filterId,
        })),
      toggleOpen: () => setState(s => ({ ...s, open: !s.open })),
      close: () => setState(s => ({ ...s, open: false })),
      clearFilters: () => {
        const keysWithOnReset = Object.keys(state.filterValues).filter(k => filters[k].onReset);
        const postResetValues = Object.fromEntries(
          keysWithOnReset.map(k => [k, filters[k].onReset!(state.filterValues[k])]),
        );
        const newState = {
          ...state,
          filterValues: makeEmptyState(filters, postResetValues as T),
        };

        setState(newState);
        if (onChange) {
          onChange({ id: null, value: null }, newState.filterValues);
        }
      },
    },
  };

  return actions;
};

export default useInternalFilterState;
